/**
 * THE condition builder — Step 42.
 *
 * One control, three callers: the automation rule builder (Step 89), the SLA
 * policy editor (Step 31) and the ticket queue's advanced filter. All three
 * had grown their own copy, and the copies had already drifted in ways Kai
 * found one at a time:
 *
 *   - *"That's crap, I can't multi select"* — the automation copy rendered a
 *     single-select for `is any of`, an operator whose entire purpose is
 *     picking several. The SLA copy had had multi-select for weeks.
 *   - *"How do I and and or statements"* — stacked clauses could only express
 *     AND. The SLA copy already had groups.
 *   - Three of the grammar's eight operators were missing from the automation
 *     copy, `contains` among them.
 *
 * Every one of those was a fix applied once and not carried across. The
 * grammar has been shared since Step 28; the CONTROL is what was duplicated,
 * and this is the file that stops it happening a fourth time.
 *
 * ── What the caller supplies ───────────────────────────────────────────
 *
 * Only what genuinely differs: the field list, and how to resolve options for
 * a field whose values come from the database (a group, an agent, a status).
 * Everything else — the grammar, the operator set, which operators take a
 * list, which force free text, how a single clause collapses back to a bare
 * condition — belongs to the grammar and lives here.
 */
import { Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select } from './ui/select';
import { Combobox } from './ui/combobox';
import { TokenSelect } from './ui/tokenselect';
import type { Condition } from '@itsm/shared';

export interface ConditionField {
  key: string;
  label: string;
  /** Fixed options, when the field has them. Otherwise `valueOptions` is
   *  asked, and a plain text box is the fallback. */
  options?: { value: string; label: string }[];
  /** Operators this field cannot support — SLA state has no "contains", and
   *  a status is never empty. Offering an operator the server will refuse is
   *  a filter somebody builds and cannot save. */
  omitOps?: string[];
  /** Step 120 — a number (hours, counts). Offers the comparison operators
   *  and a number box, and hides the text ones. */
  numeric?: boolean;
}

/** Every operator the shared grammar evaluates. Not a subset: a builder that
 *  offers fewer than the engine understands is a builder with a secret. */
export const CONDITION_OPS = [
  { op: 'eq', label: 'is' },
  { op: 'neq', label: 'is not' },
  { op: 'in', label: 'is any of' },
  { op: 'notIn', label: 'is none of' },
  { op: 'contains', label: 'contains' },
  { op: 'notContains', label: 'does not contain' },
  { op: 'gt', label: 'is more than' },
  { op: 'gte', label: 'is at least' },
  { op: 'lt', label: 'is less than' },
  { op: 'lte', label: 'is at most' },
  { op: 'isEmpty', label: 'is empty' },
  { op: 'isNotEmpty', label: 'is not empty' },
] as const;

/** Operators that take a LIST rather than a single value. */
export const MULTI_OPS = new Set(['in', 'notIn']);
/** Operators that are always free text — "contains Heart FM" is a substring,
 *  not a pick from a list, even on a field that has options. */
export const TEXT_OPS = new Set(['contains', 'notContains']);
/** Operators that take no value at all. */
export const NO_VALUE_OPS = new Set(['isEmpty', 'isNotEmpty']);
/** Step 120 — operators that compare numbers. Offered only on numeric fields;
 *  a numeric field offers these, is/is not and the empty pair, never
 *  "contains". */
export const NUMERIC_OPS = new Set(['gt', 'gte', 'lt', 'lte']);
const NUMERIC_FIELD_OPS = new Set(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'isEmpty', 'isNotEmpty']);

type Clause = { op: string; fieldKey: string; value?: unknown };
type Node = Clause | { op: 'and' | 'or'; conditions: Node[] };
const isNodeGroup = (n: Node): n is { op: 'and' | 'or'; conditions: Node[] } =>
  n.op === 'and' || n.op === 'or';

/** How deep the UI lets you nest. The server allows six; two is what a person
 *  can still read back as a sentence, and "(A and B) or (C and D)" is the
 *  shape real policies actually need — Kai: *"I should be able to set AND
 *  this AND that OR THIS or THAT type policy"*. */
export const MAX_GROUP_DEPTH = 2;

/** Read either shape. A bare clause and a group are both legal storage; the
 *  editor always works on the group shape and collapses back on write. */
export function toClauses(c: Condition | undefined): { mode: 'and' | 'or'; clauses: Node[] } {
  const raw = c as { op?: string; conditions?: unknown[] } | undefined;
  const isGroup = raw?.op === 'and' || raw?.op === 'or';
  return {
    mode: raw?.op === 'or' ? 'or' : 'and',
    clauses: (isGroup ? ((raw?.conditions ?? []) as Node[]) : raw ? [raw as unknown as Node] : []).filter(
      Boolean,
    ),
  };
}

export function ConditionBuilder({
  value,
  onChange,
  fields,
  valueOptions,
  /** What "add another" produces. Defaults to the first field. */
  defaultFieldKey,
  addLabel,
  nestDepth,
  layout = 'inline',
}: {
  value: Condition | undefined;
  onChange: (c: Condition | undefined) => void;
  fields: ConditionField[];
  valueOptions?: (fieldKey: string) => { value: string; label: string }[];
  defaultFieldKey?: string;
  addLabel?: string;
  /** Set by the component on itself when it renders a nested group. Callers
   *  leave it alone. */
  nestDepth?: number;
  /**
   * `inline` — field · operator · value on one line. Right for the automation
   * rule builder and the SLA editor, where a condition is one step among many
   * in a narrow column.
   *
   * `stacked` — the FIELD NAME as a label with its control beneath, which is
   * how the reference's filter panel reads (`View` / `Group` / `Tags`, each a
   * heading over its own picker). Kai: *"I don't want a simple & advanced
   * filter system, I want the advanced to look better, like the captures"* —
   * so this is one builder with two layouts, not a second filter UI.
   */
  layout?: 'inline' | 'stacked';
}) {
  const { mode, clauses } = toClauses(value);
  const depth = nestDepth ?? 0;

  const write = (list: Node[], nextMode: 'and' | 'or' = mode) => {
    // A group with nothing in it is dropped, not stored — an empty `and`
    // matches everything and would silently widen whatever it guards.
    const clean = list.filter((c) => (isNodeGroup(c) ? c.conditions.length > 0 : !!c.fieldKey));
    if (clean.length === 0) return onChange(undefined);
    // Collapse a single clause back to a bare condition — a one-member `and`
    // is noise in storage and in every sentence rendered from it.
    onChange((clean.length === 1 ? clean[0] : { op: nextMode, conditions: clean }) as unknown as Condition);
  };

  const firstField = defaultFieldKey ?? fields[0]?.key ?? '';

  return (
    <div className="space-y-2">
      {clauses.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Match</span>
          <Select
            value={mode}
            onChange={(e) => write(clauses, e.target.value as 'and' | 'or')}
            className="h-7 w-28 text-xs"
            aria-label="Match all or any"
          >
            <option value="and">all of</option>
            <option value="or">any of</option>
          </Select>
          <span className="text-xs text-muted-foreground">these</span>
        </div>
      )}

      {(clauses as Node[]).map((c, i) => {
        // A nested group renders the same control again, indented. Recursion
        // rather than a second layout: "(A and B) or C" has to read the same
        // way at every level or nobody trusts what they built.
        if (isNodeGroup(c)) {
          return (
            <div key={i} className="flex items-start gap-2">
              <span className="w-10 shrink-0 pt-2 text-xs text-muted-foreground">
                {i === 0 ? '' : mode === 'or' ? 'or' : 'and'}
              </span>
              <div className="min-w-0 flex-1 rounded-md border border-dashed bg-muted/20 p-2">
                <ConditionBuilder
                  value={c as unknown as Condition}
                  onChange={(next) =>
                    write(
                      (clauses as Node[]).map((x, xi) =>
                        xi === i ? ((next ?? { op: 'and', conditions: [] }) as unknown as Node) : x,
                      ),
                    )
                  }
                  fields={fields}
                  {...(valueOptions ? { valueOptions } : {})}
                  {...(defaultFieldKey ? { defaultFieldKey } : {})}
                  nestDepth={depth + 1}
                />
              </div>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Remove group"
                onClick={() => write((clauses as Node[]).filter((_, xi) => xi !== i))}
              >
                <Trash2 />
              </Button>
            </div>
          );
        }
        const field = fields.find((f) => f.key === c.fieldKey);
        const opts = field?.options ?? valueOptions?.(c.fieldKey) ?? [];
        const ops = CONDITION_OPS.filter((o) => !field?.omitOps?.includes(o.op)).filter((o) =>
          field?.numeric ? NUMERIC_FIELD_OPS.has(o.op) : !NUMERIC_OPS.has(o.op),
        );
        const numeric = !!field?.numeric || NUMERIC_OPS.has(c.op);
        // `x` at index `i` is a clause — the group case returned above — but
        // the cast has to be explicit for the compiler to agree.
        const set = (patch: Partial<Clause>) =>
          write(
            (clauses as Node[]).map((x, xi) => (xi === i ? ({ ...(x as Clause), ...patch } as Node) : x)),
          );
        const needsValue = !NO_VALUE_OPS.has(c.op);
        const multi = MULTI_OPS.has(c.op);
        const freeText = TEXT_OPS.has(c.op);
        const selected = Array.isArray(c.value) ? (c.value as string[]) : c.value ? [String(c.value)] : [];

        if (layout === 'stacked') {
          return (
            <div key={i} className="rounded-md border bg-background/40 p-2">
              <div className="mb-1.5 flex items-center gap-1.5">
                {/* The field name IS the heading — chosen from a picker, but
                    it reads as a label rather than as one of three equal
                    dropdowns. */}
                <Select
                  value={c.fieldKey ?? ''}
                  onChange={(e) => {
                    const nextKey = e.target.value;
                    const next = fields.find((f) => f.key === nextKey);
                    const opStillValid = !next?.omitOps?.includes(c.op);
                    set({ fieldKey: nextKey, value: '', ...(opStillValid ? {} : { op: 'eq' }) });
                  }}
                  className="h-7 flex-1 border-0 bg-transparent px-0 text-xs font-semibold uppercase tracking-wider text-muted-foreground shadow-none focus-visible:ring-0"
                  aria-label="Field"
                >
                  {fields.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label}
                    </option>
                  ))}
                </Select>
                <Select
                  value={c.op ?? 'eq'}
                  onChange={(e) => {
                    const op = e.target.value;
                    set({ op, value: MULTI_OPS.has(op) ? selected : (selected[0] ?? '') });
                  }}
                  className="h-7 w-32 text-xs"
                  aria-label="Operator"
                >
                  {ops.map((o) => (
                    <option key={o.op} value={o.op}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Remove condition"
                  onClick={() => write((clauses as Node[]).filter((_, xi) => xi !== i))}
                >
                  <Trash2 />
                </Button>
              </div>
              {needsValue && (
                <label className="mb-1 block text-[11px] text-muted-foreground">
                  {field?.label ?? 'Value'}
                </label>
              )}
              {needsValue &&
                (multi ? (
                  /* PILLS, not a native multi-select. The reference renders
                     every chosen value as a removable token with an "Enter or
                     select" input beside them — which is not decoration: you
                     can see all the choices and drop one without disturbing
                     the others, where ctrl-clicking a highlighted row shows
                     you neither. */
                  <TokenSelect
                    value={selected}
                    options={opts}
                    onChange={(v) => set({ value: v })}
                    allowFree={opts.length === 0}
                    ariaLabel="Value"
                  />
                ) : opts.length > 0 && !freeText ? (
                  <Combobox
                    value={selected[0] ?? null}
                    options={opts}
                    onChange={(v) => set({ value: v ?? '' })}
                    placeholder="Enter or select"
                  />
                ) : numeric ? (
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={selected[0] === undefined || selected[0] === null ? '' : String(selected[0])}
                    onChange={(e) => set({ value: e.target.value === '' ? '' : Number(e.target.value) })}
                    className="h-9 w-full text-xs"
                    aria-label="Value"
                  />
                ) : (
                  <Input
                    value={String(selected[0] ?? '')}
                    onChange={(e) => set({ value: e.target.value })}
                    className="h-9 w-full text-xs"
                    aria-label="Value"
                  />
                ))}
              {i > 0 && (
                <p className="pt-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                  {mode === 'or' ? 'or' : 'and'} …
                </p>
              )}
            </div>
          );
        }

        return (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <span className="w-10 shrink-0 text-xs text-muted-foreground">
              {i === 0 ? '' : mode === 'or' ? 'or' : 'and'}
            </span>
            <Select
              value={c.fieldKey ?? ''}
              onChange={(e) => {
                const nextKey = e.target.value;
                const next = fields.find((f) => f.key === nextKey);
                // Changing the field may invalidate the operator too — a
                // status has no "is empty", and leaving one selected produces
                // a filter the server refuses on save.
                const opStillValid = !next?.omitOps?.includes(c.op);
                set({ fieldKey: nextKey, value: '', ...(opStillValid ? {} : { op: 'eq' }) });
              }}
              className="h-8 w-40"
              aria-label="Field"
            >
              {fields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </Select>
            <Select
              value={c.op ?? 'eq'}
              onChange={(e) => {
                const op = e.target.value;
                // Converting rather than clearing keeps the choice already made.
                set({ op, value: MULTI_OPS.has(op) ? selected : (selected[0] ?? '') });
              }}
              className="h-8 w-36"
              aria-label="Operator"
            >
              {ops.map((o) => (
                <option key={o.op} value={o.op}>
                  {o.label}
                </option>
              ))}
            </Select>
            {needsValue &&
              (opts.length > 0 && !freeText ? (
                <Select
                  multiple={multi}
                  value={multi ? (selected as unknown as string) : (selected[0] ?? '')}
                  onChange={(e) =>
                    set({
                      value: multi
                        ? Array.from(e.target.selectedOptions).map((o) => o.value)
                        : e.target.value,
                    })
                  }
                  className={multi ? 'h-24 w-48' : 'h-8 w-48'}
                  aria-label="Value"
                >
                  {!multi && <option value="">Choose…</option>}
                  {opts.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  value={String(selected[0] ?? '')}
                  onChange={(e) => set({ value: e.target.value })}
                  className="h-8 w-48"
                  aria-label="Value"
                />
              ))}
            <Button
              size="sm"
              variant="ghost"
              aria-label="Remove condition"
              onClick={() => write((clauses as Node[]).filter((_, xi) => xi !== i))}
            >
              <Trash2 />
            </Button>
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-2">
        {layout === 'stacked' ? (
          /* A hyperlink-style Add, as the reference has it
             (`.btn-as-hyperlink .btn-add-condition`) — a bordered button here
             competes with the condition blocks it sits under. */
          <button
            onClick={() => write([...(clauses as Node[]), { op: 'eq', fieldKey: firstField, value: '' }])}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Plus className="h-3.5 w-3.5" />
            {clauses.length === 0 ? (addLabel ?? 'Add a condition') : 'Add'}
          </button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => write([...(clauses as Node[]), { op: 'eq', fieldKey: firstField, value: '' }])}
          >
            <Plus /> {clauses.length === 0 ? (addLabel ?? 'Add a condition') : mode === 'or' ? 'or…' : 'and…'}
          </Button>
        )}
        {/* A GROUP, so "(A and B) or (C and D)" is expressible. Offered only
            where it can still be read back — one level of nesting, and only
            once there is something for it to sit beside. */}
        {depth < MAX_GROUP_DEPTH - 1 && clauses.length > 0 && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              write([
                ...(clauses as Node[]),
                {
                  op: mode === 'or' ? 'and' : 'or',
                  conditions: [{ op: 'eq', fieldKey: firstField, value: '' }],
                },
              ])
            }
          >
            <Plus /> group
          </Button>
        )}
      </div>
    </div>
  );
}
